import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';
import { VOID } from '../../rxjs-utils';
import { SerializableParams } from '../../utils';

import { EmptyResponse } from '../data/empty-response.do';
import { MobizHttpErrorResponse } from '../data/mobiz-http-error-response.do';
import { MobizSession } from '../data/mobiz-session.do';
import { Organization } from '../data/organization.do';
import { User } from '../data/user.do';
import { OAuthLoginError } from '../errors/oauth-login.error';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { MobizServerApi } from '../other/mobiz-server-api.enum';

import {
  IAuthGrantClientCredentialsGrantTypeSecret,
  IAuthGrantPasswordGrantTypeSecret,
  IAuthTokenService,
} from './auth-token.interface';
import { IAuthService } from './auth.interface';
import { CONFIGURATION_SERVICE, IConfigurationService } from './configuration.interface';
import { IMobizStateService } from './mobiz-state.interface';
import { IMobizStorageService } from './mobiz-storage.interface';
import { IServerApiBuilderService } from './server-api-builder.interface';
import { AuthGrantType } from './auth-token-grant';
import { TextResponse } from '../data/text-response.do';
import {AudkenniCode} from "../data/audkenni-code.do";

@Injectable()
export class AuthService implements IAuthService {
  constructor(
    @Inject(IMobizHttpService) protected http: IMobizHttpService,
    @Inject(IAuthTokenService) protected tokenService: IAuthTokenService,
    @Inject(CONFIGURATION_SERVICE) protected configuration: IConfigurationService,
    @Inject(IMobizStorageService) protected storage: IMobizStorageService,
    @Inject(IMobizStateService) protected state: IMobizStateService,
    @Inject(IServerApiBuilderService) protected apiBuilder: IServerApiBuilderService,
  ) {
  }

  public isAdmin(): boolean {
    return this.tokenService.session.getClaimsForAccessToken().userIsAdmin();
  }

  public isLoggedIn(): boolean {
    const session = this.tokenService.session;
    return session ? session.grantType === AuthGrantType.Password : false;
  }

  public isLoggedInUsingClientCredentials(): boolean {
    const session = this.tokenService.session;
    return session ? session.grantType === AuthGrantType.ClientCredentials : false;
  }

  public logIn(credentials: IAuthGrantPasswordGrantTypeSecret): Observable<MobizSession> {
    const payload = new SerializableParams({
      client_id: this.configuration.clientId,
      grant_type: AuthGrantType.Password,
      username: credentials.username,
      password: credentials.password,
    });
    return this.performLogin({
      serverApi: MobizServerApi.AccountLogIn,
      body: payload,
    });
  }

  public logInUsingClientCredentials(credentials: IAuthGrantClientCredentialsGrantTypeSecret): Observable<MobizSession> {
    if (!this.configuration.clientCredentialsClientId) {
      throw new Error('The clientCredentialsClientId configuration is missing.');
    }
    const payload = new SerializableParams({
      client_id: this.configuration.clientCredentialsClientId,
      grant_type: AuthGrantType.ClientCredentials,
      client_secret: credentials.client_secret,
    });
    return this.performLogin({
      serverApi: MobizServerApi.AccountLogIn,
      body: payload,
    });
  }

  public logOut(): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountLogOut,
    };

    return this.http.get(new EmptyResponse(), config).pipe(
      catchError((): Observable<void> => {
        // Ignore all API errors and just clear the local data.
        return VOID;
      }),
      map((): EmptyResponse => {
        this.tokenService.clearAuthData();
        this.storage.clear();
        return new EmptyResponse();
      }),
    );
  }

  public forgotPassword(email: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountForgotPassword,
      body: JSON.stringify({ Email: email }),
    };

    return this.http.post(new EmptyResponse(), config).pipe(
      map((response: EmptyResponse): EmptyResponse => {
        this.tokenService.clearAuthData();
        return response;
      }),
    );
  }

  public getUser(userId: string): Observable<User> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.User,
      urlParameters: { userId: userId },
    };

    return this.http.get(new User(), config);
  }

  public getOrganization(organizationId: string): Observable<Organization> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Organization,
      urlParameters: { organizationId: organizationId },
    };

    return this.http.get(new Organization(), config);
  }

  public refreshToken(): Observable<void> {
    return this.http.refreshToken();
  }

  public refreshTokenInBackground(): void {
    return this.http.refreshTokenInBackground();
  }
  
  public getAudkenniCode(): Observable<AudkenniCode> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AudkenniVerificationCode,
    };
    return this.http.get(new AudkenniCode(), config);
  }
  
  public initializeAuthPoll(idValue: string, hash: string, useAudkenniApp: boolean): Observable<TextResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: useAudkenniApp ? MobizServerApi.ECertStartAppPolling : MobizServerApi.ECertStartSimPolling,
      queryParameters:
        {
          [useAudkenniApp ? 'identificationNumber': 'phoneNumber']: idValue,
          hash: hash
        },
    };
    return this.http.post(new TextResponse(), config).pipe(
      map(response => {
        return response;
      })
    );
  }
  
  public pollAuthStatus(verificationId: string, noTimeout=false): Observable<TextResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.ECertPoll,
      queryParameters: { verificationId },
      ...(!noTimeout && { timeout: 7000 }), // Set 7 second timeout on each poll HTTP request
    };
    return this.http.get(new TextResponse(), config).pipe(
      map(response => {
        return response;
      })
    );
  }
  
  private performLogin(configuration: ApiEndpointConfiguration): Observable<MobizSession> {
    this.storage.clear();
    return this.http.post(new MobizSession(), configuration).pipe(
      map((session: MobizSession): MobizSession => {
        this.tokenService.session = session;
        return session;
      }),
      catchError((originalError: MobizHttpErrorResponse | unknown): Observable<never> => {
        // Use special OAuthLoginError handling for HTTP error responses.
        if (originalError instanceof MobizHttpErrorResponse) {
          return throwError(new OAuthLoginError(originalError.message, originalError));
        }
        return throwError(originalError);
      }),
    );
  }

}
